import { useEffect, useState, useRef } from 'react'
import { usePostHog } from 'posthog-js/react'
import { useConsent } from '@/hooks/useConsent'

export const useSessionReplay = () => {
  const posthog = usePostHog()
  const { hasConsent } = useConsent()
  const [isRecording, setIsRecording] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Effect to handle consent changes
  useEffect(() => {
    if (!posthog) return

    if (hasConsent) {
      posthog.startSessionRecording()
    } else {
      posthog.stopSessionRecording()
      setIsRecording(false)
      setSessionId(null)
    }
  }, [posthog, hasConsent])

  useEffect(() => {
    if (!posthog || !hasConsent) return

    // Check recording status periodically
    const checkStatus = () => {
      if (!posthog || !hasConsent) return

      const currentSessionId = posthog.get_session_id()
      const recording = currentSessionId !== null && hasConsent

      if (recording !== isRecording) {
        console.log('[Session Replay] Recording status changed:', {
          isRecording: recording,
          sessionId: currentSessionId,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          consentStatus: hasConsent
        })
        setIsRecording(recording)
        setSessionId(currentSessionId)
      }
    }

    // Check immediately
    checkStatus()

    // Set up periodic checking (every 5 seconds)
    intervalRef.current = setInterval(checkStatus, 5000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [posthog, isRecording, hasConsent])

  // Handle page visibility changes
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (posthog && isRecording) {
        console.log('[Session Replay] Page unloading, stopping recording')
        posthog.stopSessionRecording()
      }
    }

    const handleVisibilityChange = () => {
      if (!hasConsent) return

      if (document.visibilityState === 'hidden' && posthog && isRecording) {
        console.log('[Session Replay] Page hidden, stopping recording')
        posthog.stopSessionRecording()
      } else if (document.visibilityState === 'visible' && posthog && !isRecording && hasConsent) {
        console.log('[Session Replay] Page visible again, resuming recording')
        posthog.startSessionRecording()
        setIsRecording(true)
        setSessionId(posthog.get_session_id())
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [posthog, isRecording, hasConsent])

  return {
    isRecording,
    sessionId,
    hasConsent
  }
}